import { castArray } from 'lodash';
import { maybe } from '@passionware/monads';
import moment from 'moment-timezone';
import { CustomFieldDefinition } from '../../../__types__';
import customFieldHelper from '../../../v1/helpers/customFieldHelper';
import { resolveCustomFieldValue } from 'v5/core/custom-fields/resolveCustomFieldValue';
import { FrontDeskEntry } from './front-desk.api';
import { Booking$, ProductionCustomField$ } from './front-desk.api.http.schema';

/**
 * Front desk has no browsing workspace lens from Satie today — resolve global
 * values only (workspaceId null).
 */
function resolveStudios(
  entry: Booking$,
  custom_field_definitions: CustomFieldDefinition[]
) {
  const studioFieldDefinition = custom_field_definitions.find(
    cfd => cfd.name === 'Studio'
  );

  if (!studioFieldDefinition)
    return { definedAt: 'booking' as const, labels: [] };

  const productionStudioField = resolveCustomFieldValue(
    entry.production_custom_fields,
    studioFieldDefinition.id,
    null
  ) as ProductionCustomField$ | null;

  const bookingStudioField = resolveCustomFieldValue(
    (entry.booking_custom_fields ?? []) as ProductionCustomField$[],
    studioFieldDefinition.id,
    null
  );

  const getOptionIds = (cfd: ProductionCustomField$): number[] => {
    const value = customFieldHelper.getValue(
      cfd,
      studioFieldDefinition.data_type
    );
    return maybe.mapOrElse(value, castArray, []);
  };

  const optionToOptionValue = (cfd: ProductionCustomField$) => {
    const optionIds = getOptionIds(cfd);
    return optionIds.map(
      id =>
        studioFieldDefinition.options.find(o => o.id === id)?.value ?? 'Unknown'
    );
  };

  if (
    productionStudioField &&
    customFieldHelper.getValue(
      productionStudioField,
      studioFieldDefinition.data_type
    ) !== null
  ) {
    return {
      definedAt: 'production' as const,
      labels: optionToOptionValue(productionStudioField)
    };
  }

  if (
    bookingStudioField &&
    customFieldHelper.getValue(
      bookingStudioField,
      studioFieldDefinition.data_type
    ) !== null
  ) {
    return {
      definedAt: 'booking' as const,
      labels: optionToOptionValue(bookingStudioField as ProductionCustomField$)
    };
  }

  return {
    definedAt: 'booking' as const,
    labels: []
  };
}

export function convertToFrontDeskEntry(
  booking: Booking$,
  custom_field_definitions: CustomFieldDefinition[]
): FrontDeskEntry {
  let resolvedStudios = resolveStudios(booking, custom_field_definitions);
  // Determine checkin time and set it in start_calculated
  let start_calculated = moment('8:30', 'LT');
  if (booking.date_type === 'RECURRING_SPECIFIC_HOURS') {
    start_calculated = moment(booking.estimated_start_time, 'LTS');
  }

  return {
    time: start_calculated,
    bookingId: booking.booking_id,
    resource: {
      id: booking.contact_id,
      resourceTypeId: booking.resource_type_id,
      firstName: booking.first_name,
      lastName: booking.last_name,
      pronounsOptionId: booking.pronouns_option_id,
      profilePicture: booking.profile_picture ?? null
    },
    locations: booking.locations.filter(maybe.isPresent),
    studios: resolvedStudios.labels.map(label => ({
      definedAt: resolvedStudios.definedAt,
      name: label
    })),
    checkinDate: maybe.mapOrNull(booking.checkin?.checked_in_timestamp, moment),
    checkoutDate: maybe.mapOrNull(
      booking.checkin?.checked_out_timestamp,
      moment
    ),
    badgeNumber: maybe.getOrNull(booking.checkin?.badge_number),
    note: booking.note ?? '',
    status: maybe.getOrNull(booking.checkin?.status),
    checkinId: maybe.getOrNull(booking.checkin?.id)
  };
}
